<template>
	<div class="card" style="min-height: 70vh;">
		<div class="card-body">
			<!-- Mapping Table -->
			<div class="table-responsive" style="min-height: calc(70vh - 50px);">
				<table class="table table-hover">
					<thead class="thead-light">
						<tr>
							<th style="width: 50px;">#</th>
							<th style="width: 40%;">Data dari File</th>
							<th style="width: calc(100% - 50px - 40% - 120px);">Product Suplier</th>
							<th style="width: 120px;">Status</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="(row, index) in mappingDataPerProduct"
							:key="index"
							:class="{ 'table-danger': !isRowValid(row) }"
						>
							<td>{{ index + 1 }}</td>
							<td>
								<div class="small">
									<strong>{{ row.productName || '-' }}</strong>
								</div>
							</td>
							<td>
								<div class="product-selector">
									<!-- Selected Products as Pills -->
									<div class="selected-products mb-2">
										<span
											v-for="product in row.selectedProducts"
											:key="product._id"
											class="product-pill"
										>
											{{ product.name }}
											<button
												type="button"
												class="close-btn"
												@click="removeProduct(index, product)"
												:disabled="isProcessing"
											>
												<i class="material-icons" style="font-size: 14px;">close</i>
											</button>
										</span>
									</div>

									<!-- Button to Open Modal -->
									<button
										type="button"
										class="btn btn-sm btn-outline-primary"
										@click="openProductModal(index)"
										:disabled="isProcessing"
									>
										<i class="material-icons" style="font-size: 16px; vertical-align: middle;">add</i>
										Pilih Produk
									</button>
								</div>
							</td>
							<td>
								<span v-if="isRowValid(row)" class="badge badge-success">
									<i class="material-icons" style="font-size: 14px;">check_circle</i>
								</span>
								<span v-else class="badge badge-danger">
									<i class="material-icons" style="font-size: 14px;">error</i>
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Product Selection Modal -->
			<div
				v-if="showModal"
				class="modal-overlay"
				@click="closeProductModal"
			>
				<div class="modal-content" @click.stop>
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="material-icons" style="font-size: 20px; vertical-align: middle;">shopping_cart</i>
							Product Suplier
						</h5>
						<button
							type="button"
							class="close"
							@click="closeProductModal"
						>
							<i class="material-icons" style="font-size: 24px;">close</i>
						</button>
					</div>

					<div class="modal-body">
						<!-- Search Input -->
						<div class="form-group">
							<div class="input-group">
								<div class="input-group-prepend">
									<span class="input-group-text">
										<i class="material-icons" style="font-size: 18px;">search</i>
									</span>
								</div>
								<input
									type="text"
									class="form-control"
									placeholder="Cari produk berdasarkan nama atau kode..."
									v-model="searchQuery"
									@input="handleSearch"
									ref="searchInput"
								>
							</div>
						</div>

						<!-- Product List -->
						<div class="product-list">
							<div
								v-for="product in filteredProducts"
								:key="product._id"
								class="product-item"
								:class="{ 'selected': isProductSelected(product) }"
								@click="toggleProduct(product)"
							>
								<div class="d-flex justify-content-between align-items-center">
									<div>
										<strong>{{ product.name }}</strong>
										<br>
										<small class="text-muted">{{ product.code }}</small>
									</div>
									<div class="d-flex align-items-center">
										<div class="text-right mr-3">
											<div class="font-weight-bold">{{ getProductPrice(product) }}</div>
										</div>
										<div class="checkbox-wrapper">
											<input
												type="checkbox"
												:checked="isProductSelected(product)"
												@click.stop="toggleProduct(product)"
											>
										</div>
									</div>
								</div>
							</div>

							<div v-if="filteredProducts.length === 0" class="text-center text-muted py-4">
								<i class="material-icons" style="font-size: 48px; opacity: 0.5;">search_off</i>
								<p class="mt-2">Tidak ada produk ditemukan</p>
							</div>
						</div>
					</div>

					<div class="modal-footer">
						<div class="selected-info">
							{{ selectedProductsInModal.length }} produk dipilih
						</div>
						<div>
							<button
								type="button"
								class="btn btn-secondary"
								@click="closeProductModal"
							>
								Batal
							</button>
							<button
								type="button"
								class="btn btn-primary ml-2"
								@click="applyProductSelection"
								:disabled="selectedProductsInModal.length === 0"
							>
								Terapkan
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { formatMoney, getSimpleProductOriginalPrice } from '@/lib/helper';

export default {
	name: 'Step2ProductMapping',
	data() {
		return {
			showModal: false,
			currentEditIndex: null,
			searchQuery: '',
			selectedProductsInModal: [],
			filteredProducts: [],
		};
	},
	computed: {
		...mapGetters('importWholesaleOrder', [
			'mappingDataPerProduct',
			'availableProducts',
			'isProcessing',
			'mappedCount',
			'unmappedCount',
		]),
	},
	methods: {
		...mapActions('importWholesaleOrder', [
			'updateMappingRow',
		]),

		formatMoney,
		getSimpleProductOriginalPrice,

		isRowValid(row) {
			return row.selectedProducts && row.selectedProducts.length > 0;
		},

		openProductModal(index) {
			this.currentEditIndex = index;
			this.searchQuery = '';
			// Clone the selected products for the current row
			this.selectedProductsInModal = [...(this.mappingDataPerProduct[index].selectedProducts || [])];
			this.filterProducts();

			this.showModal = true;

			// Focus on search input after modal opens
			this.$nextTick(() => {
				if (this.$refs.searchInput) {
					this.$refs.searchInput.focus();
				}
			});
		},

		closeProductModal() {
			this.showModal = false;
			this.currentEditIndex = null;
			this.searchQuery = '';
			this.selectedProductsInModal = [];
		},

		handleSearch() {
			this.filterProducts();
		},

		filterProducts() {
			const query = this.searchQuery.toLowerCase().trim();

			if (!query) {
				this.filteredProducts = this.availableProducts;
				return;
			}

			this.filteredProducts = this.availableProducts.filter(product => (
				product.name.toLowerCase().includes(query)
					|| product.code.toLowerCase().includes(query)
			));
		},

		isProductSelected(product) {
			return this.selectedProductsInModal.some(p => p._id === product._id);
		},

		toggleProduct(product) {
			const index = this.selectedProductsInModal.findIndex(p => p._id === product._id);

			if (index > -1) {
				// Remove if already selected
				this.selectedProductsInModal.splice(index, 1);
			} else {
				// Add if not selected
				this.selectedProductsInModal.push(product);
			}
		},

		applyProductSelection() {
			if (this.currentEditIndex !== null) {
				this.updateMappingRow({
					index: this.currentEditIndex,
					productIds: this.selectedProductsInModal,
				});
			}
			this.closeProductModal();
		},

		removeProduct(index, product) {
			const currentProducts = [...this.mappingDataPerProduct[index].selectedProducts];
			const updatedProducts = currentProducts.filter(p => p._id !== product._id);

			this.updateMappingRow({
				index,
				productIds: updatedProducts,
			});
		},

		getTotalPrice(products) {
			if (!products || !products.length) return 0;
			return products.reduce((sum, product) => sum + getSimpleProductOriginalPrice(product), 0);
		},

		getProductPrice(product) {
			const price = getSimpleProductOriginalPrice(product);
			return formatMoney(price);
		},
	},
};
</script>

<style scoped>
.table td, .table th {
	vertical-align: middle;
}

/* Product Selector */
.product-selector {
	min-width: 200px;
}

.selected-products {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.product-pill {
	display: inline-flex;
	align-items: center;
	background-color: #276dc7;
	color: white;
	padding: 4px 10px;
	border-radius: 20px;
	font-size: 0.875rem;
	font-weight: 500;
	gap: 8px;
}

.product-pill .close-btn {
	background: none;
	border: none;
	color: white;
	cursor: pointer;
	padding: 0;
	display: flex;
	align-items: center;
	opacity: 0.8;
	transition: opacity 0.2s;
	line-height: 1;
}

.product-pill .close-btn:hover {
	opacity: 1;
}

.product-pill .close-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Modal Overlay */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1050;
	padding: 20px;
}

.modal-content {
	background: white;
	border-radius: 8px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	max-width: 700px;
	width: 100%;
	max-height: 80vh;
	display: flex;
	flex-direction: column;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid #dee2e6;
}

.modal-title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 600;
}

.modal-header .close {
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	color: #6c757d;
	transition: color 0.2s;
	display: flex;
	align-items: center;
}

.modal-header .close:hover {
	color: #000;
}

.modal-body {
	flex: 1;
	overflow-y: auto;
	padding: 20px;
}

.modal-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px 20px;
	border-top: 1px solid #dee2e6;
}

.selected-info {
	color: #6c757d;
	font-size: 0.875rem;
}

/* Product List */
.product-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-top: 15px;
}

.product-item {
	padding: 12px 16px;
	border: 2px solid #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s;
	background: white;
}

.product-item:hover {
	border-color: #276dc7;
	background-color: #f8f9fa;
}

.product-item.selected {
	border-color: #276dc7;
	background-color: #e7f1ff;
}

.product-item .checkbox-wrapper input[type="checkbox"] {
	width: 18px;
	height: 18px;
	cursor: pointer;
}

/* Input Group */
.input-group-text {
	background-color: #f8f9fa;
}

.input-group .form-control:focus {
	box-shadow: none;
	border-color: #276dc7;
}

.input-group .form-control:focus + .input-group-prepend .input-group-text,
.input-group .input-group-prepend .input-group-text + .form-control:focus {
	border-color: #276dc7;
}
</style>
